import { useEffect, useRef, useState } from "react";
import { Bell, Volume2, VolumeX } from "lucide-react";
import { fmtTime } from "./advisorUtils";

// Header bell: unread count badge + a "what did I miss" list + mute toggle.
export const DeskAlertsBell = ({ recent, unread, onSeen, muted, onToggleMute, onOpen }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    onSeen();
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300"
        title="Recent alerts"
        data-testid="desk-alerts-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-emerald-500 text-slate-950 text-xs font-black flex items-center justify-center" data-testid="desk-alerts-unread">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[90vw] rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl z-30 overflow-hidden" data-testid="desk-alerts-panel">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <span className="font-bold text-slate-200">Recent alerts</span>
            <button
              type="button"
              onClick={onToggleMute}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${muted ? "bg-slate-800 text-slate-400" : "bg-emerald-500/15 text-emerald-300"}`}
              data-testid="desk-alerts-mute-toggle"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {muted ? "Chime off" : "Chime on"}
            </button>
          </div>
          <div className="max-h-[60vh] overflow-auto divide-y divide-slate-800">
            {recent.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="desk-alerts-empty">
                Nothing yet — approvals and replies will land here.
              </div>
            )}
            {recent.map((ev) => (
              <button
                key={ev.id}
                type="button"
                onClick={() => { setOpen(false); onOpen(ev); }}
                className="w-full text-left px-4 py-3 hover:bg-slate-800/70 active:bg-slate-800"
                data-testid={`desk-alerts-item-${ev.id}`}
              >
                <div className="font-bold text-slate-100 text-sm leading-tight">{ev.title}</div>
                <div className="text-xs text-slate-400 mt-0.5 truncate">{ev.body}</div>
                <div className="text-[11px] text-slate-500 mt-1">{fmtTime(ev.at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
